/**
 * Display-side pickup pricing, mirroring the server-side charge: the bundle
 * discount (earned by attaching to an existing pickup run) is applied to the
 * fee, and tax is charged on the NET. Single source for the pricing step,
 * payment step, and payment sidebar so every screen shows the same total the
 * card is actually charged.
 *
 * ⚠️ Prices come from the QUOTE TIER, never from the request body.
 *
 * This used to read `pickup.pickupPriceCents` — off the object being POSTed. That
 * is how the pickup price came to be on the wire at all: a screen needed it for
 * display, so it rode along in the request, where the server then trusted it. Every
 * caller already had the tier in hand; now they pass it.
 */
export function pickupDisplayCents(
  tier:
    | { pickupPriceCents: number; taxCents: number; taxFraction?: number }
    | null
    | undefined,
  /** Display-only. The server re-derives this at placement; it is never sent. */
  bundleDiscountCents: number,
  include: boolean
): { pickupCents: number; discountCents: number; taxCents: number } {
  if (!include || !tier) {
    return { pickupCents: 0, discountCents: 0, taxCents: 0 };
  }
  const tierTaxCents = tier.taxCents;
  const taxFraction = tier.taxFraction;
  const raw = tier.pickupPriceCents ?? 0;
  const discount = Math.min(bundleDiscountCents, raw);
  const net = raw - discount;
  let taxCents: number;
  if (discount === 0) {
    // Undiscounted: show the server's exact figure untouched.
    taxCents = tierTaxCents;
  } else if (typeof taxFraction === "number") {
    // Mirrors the charge exactly: round(net × fraction).
    taxCents = Math.round(net * taxFraction);
  } else {
    // Legacy quotes without taxFraction: best-effort scaling of rounded tax.
    taxCents = raw > 0 ? Math.round((tierTaxCents * net) / raw) : tierTaxCents;
  }
  return { pickupCents: raw, discountCents: discount, taxCents };
}
